import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, limit, query } from 'firebase/firestore';
import { db } from '../lib/firebase';
import Login from './Login';
import AdminSetup from './AdminSetup';
import MaintenancePage from './MaintenancePage';

const ProtectedRoute = ({ children, requireAdmin = false, maintenanceStatus = null }) => {
  const { currentUser, userRole, loading } = useAuth();
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [hasUsers, setHasUsers] = useState(false);

  useEffect(() => {
    const checkSetupStatus = async () => {
      // Skip checking if user is already authenticated
      if (currentUser) {
        setHasUsers(true);
        setCheckingSetup(false);
        return;
      }

      try {
        // Try to get any document from users collection
        const usersQuery = query(collection(db, 'users'), limit(1));
        const snapshot = await getDocs(usersQuery);
        setHasUsers(!snapshot.empty);
        
        // Also check localStorage as backup
        const localSetupComplete = localStorage.getItem('stb-setup-complete') === 'true';
        if (localSetupComplete && snapshot.empty) {
          // If localStorage says setup is complete but no users in DB, trust localStorage
          setHasUsers(true);
        }
      } catch (error) {
        console.error('Error checking for users:', error);
        // If there's an error, check localStorage as fallback
        const localSetupComplete = localStorage.getItem('stb-setup-complete') === 'true';
        setHasUsers(localSetupComplete);
      } finally {
        setCheckingSetup(false);
      }
    };

    checkSetupStatus();
  }, [currentUser]);

  if (loading || checkingSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Show admin setup if no users exist and no current user
  if (!hasUsers && !currentUser) {
    return <AdminSetup />;
  }

  if (!currentUser) {
    return <Login />;
  }

  // Check maintenance mode - only block non-admin users
  if (maintenanceStatus?.isMaintenanceMode && userRole !== 'admin') {
    return <MaintenancePage message={maintenanceStatus.message} />;
  }

  if (requireAdmin && userRole !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Acceso Denegado</h2>
          <p className="text-gray-600 mb-6">No tiene permisos para acceder a esta página.</p>
          <a 
            href="/"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Volver al Dashboard
          </a>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;